import React, { useEffect, useRef } from 'react';
import { FaCheck } from 'react-icons/fa';
import './AboutUsHero.css';

// Logo-Polygone, zentriert um (0,0)
const polygons = [
  [
    [-40, -30],
    [-20, -30],
    [0, 10],
    [-20, 10],
  ],
  [
    [-15, -30],
    [5, -30],
    [35, 30],
    [15, 30],
  ],
];

const inPolygon = (x, y, poly) => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if (
      (yi > y) !== (yj > y) &&
      x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
    )
      inside = !inside;
  }
  return inside;
};

// Flaggen-Maße (lokale Einheiten)
const FLAG_LEFT = -55;
const FLAG_RIGHT = 55;
const FLAG_TOP = -40;
const FLAG_BOTTOM = 40;

const AboutUsHero = () => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;

    const ctx = canvas.getContext('2d');
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let size = 0;
    let points = [];

    const buildPoints = () => {
      points = [];
      const step = size < 380 ? 3.4 : 2.6;
      for (let x = FLAG_LEFT; x <= FLAG_RIGHT; x += step) {
        for (let y = FLAG_TOP; y <= FLAG_BOTTOM; y += step) {
          points.push({
            x,
            y,
            inside: polygons.some((p) => inPolygon(x, y, p)),
          });
        }
      }
    };

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      size = Math.min(rect.width, rect.height);
      canvas.width = size * dpr;
      canvas.height = size * dpr;
      canvas.style.width = `${size}px`;
      canvas.style.height = `${size}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildPoints();
    };

    const focal = 320;
    const displayScale = () => size / 150;

    // leichter, gedämpfter Maus-Tilt
    const tilt = { x: 0, tx: 0 };
    const onMove = (e) => {
      const rect = wrap.getBoundingClientRect();
      tilt.tx = ((e.clientX - rect.left) / rect.width - 0.5) * 0.2;
    };
    const onLeave = () => {
      tilt.tx = 0;
    };

    const draw = (time) => {
      const cx = size / 2;
      const cy = size / 2;
      const scale = displayScale();

      tilt.x += (tilt.tx - tilt.x) * 0.05;

      // leichte Grundneigung, damit die Tiefe sichtbar wird
      const A = 0.22 + tilt.x;
      const cosA = Math.cos(A);
      const sinA = Math.sin(A);

      const amp = 11; // Wind-Amplitude
      const freq = 0.085; // Wellenlänge
      const speed = 2.0; // Wind-Geschwindigkeit

      ctx.clearRect(0, 0, size, size);

      const proj = [];
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        // Amplitude nimmt zum freien Ende (rechts) zu, wie bei einer echten Fahne
        const ampF = (p.x - FLAG_LEFT) / (FLAG_RIGHT - FLAG_LEFT);
        const wave = Math.sin(p.x * freq - time * speed);
        const z = wave * amp * ampF;
        const yOff = wave * amp * 0.22 * ampF;

        const x = p.x;
        const y = p.y + yOff;
        const rx = x * cosA + z * sinA;
        const rz = -x * sinA + z * cosA;
        const persp = focal / (focal + rz * scale); // focal groß → immer > 0

        proj.push({
          sx: cx + rx * scale * persp,
          sy: cy + y * scale * persp,
          rz,
          persp,
          inside: p.inside,
          // Licht: Wellenberge (zum Betrachter) heller
          light: 0.55 + (wave * 0.5 + 0.5) * 0.45,
        });
      }

      // hinten zuerst zeichnen
      proj.sort((a, b) => b.rz - a.rz);

      for (let i = 0; i < proj.length; i++) {
        const p = proj[i];
        const radius = Math.max(0.6, (p.inside ? 1.9 : 1.4) * p.persp);

        if (p.inside) {
          // Logo: kräftiges Markengrün mit Licht-Shading
          const r = Math.round(18 + p.light * 40);
          const g = Math.round(90 + p.light * 70);
          const b = Math.round(45 + p.light * 40);
          ctx.fillStyle = `rgb(${r},${g},${b})`;
        } else {
          // Fahnenstoff: dezente helle Punkte
          ctx.fillStyle = `rgba(120, 170, 140, ${0.16 + p.light * 0.14})`;
        }

        ctx.beginPath();
        ctx.arc(p.sx, p.sy, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    let frameId = null;

    const loop = (now) => {
      draw(now * 0.001);
      frameId = requestAnimationFrame(loop);
    };

    const start = () => {
      if (frameId === null && !prefersReduced) {
        frameId = requestAnimationFrame(loop);
      }
    };
    const stop = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
    };

    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !document.hidden) start();
      else stop();
    });
    const onVis = () => (document.hidden ? stop() : start());
    const ro = new ResizeObserver(() => {
      resize();
      if (prefersReduced) draw(0);
    });

    resize();
    ro.observe(wrap);
    io.observe(wrap);
    document.addEventListener('visibilitychange', onVis);
    wrap.addEventListener('mousemove', onMove);
    wrap.addEventListener('mouseleave', onLeave);

    if (prefersReduced) draw(0);
    else start();

    return () => {
      stop();
      ro.disconnect();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
      wrap.removeEventListener('mousemove', onMove);
      wrap.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <section className="about-header">
      <div className="about-hero-grid">
        <div className="about-hero-text">
          <h1 className="about-usheader-title">Ihr Partner zum Wachsen</h1>

          <div className="bullet-points">
            <div className="bullet-item">
              <FaCheck className="check-icon" />
              <span>Qualifiziertes Team mit 100% Universitätsabsolventen</span>
            </div>
            <div className="bullet-item">
              <FaCheck className="check-icon" />
              <span>Spezialisiert auf moderne Web- &amp; App-Entwicklung</span>
            </div>
            <div className="bullet-item">
              <FaCheck className="check-icon" />
              <span>6 Jahre Projekterfahrung in der Softwareimplementierung</span>
            </div>
            <div className="bullet-item">
              <FaCheck className="check-icon" />
              <span>Agile Arbeitsweise mit Fokus auf skalierbare Lösungen</span>
            </div>
          </div>
        </div>

        <div className="about-hero-logo" ref={wrapRef}>
          <canvas
            ref={canvasRef}
            className="about-hero-logo-canvas"
            aria-hidden="true"
          />
        </div>
      </div>
    </section>
  );
};

export default AboutUsHero;